import {
  getAssessmentAssumptions,
  buildIncomeTimeline,
  getGapSummary,
  getTaxPoolSnapshot,
  getHoldingsBundle,
  analyzeFees,
  getRmdOutlook,
  analyzeTaxLocation,
  getSituationSummary,
  getProtectionChecklist,
  getPlanQuestions,
  getRothConversionOutlook,
  compareFeeAlternatives,
  getGoalSheet,
  summarizeUnpricedHoldings,
} from './assessmentSelectors.js';
import { getAllocation } from './portfolioAnalysis.js';
import { isKnown, readAmount } from './assessmentFacts.js';
import { getHouseholdProfile, getGoalLead, isFarFromRetirement } from './householdProfile.js';

// The reading behind the step-through meeting.
//
// Two people set this design on 2026-09-29, and they wanted different things.
// Eugene: the meeting should point things out, not replay the questionnaire in
// nicer type. Jake: it should support the conversation advisors already have,
// which is illustrated by hand off the tax triangle, and nothing in it may
// need an advisor to write prose per client. This module is where those meet:
// everything it says is computed by a rule from the questionnaire and the
// statements, each finding lands on the page it belongs to, and an advisor can
// leave any one of them out with a click before the client sits down.
//
// The arithmetic and every sentence come from the older report's selectors.
// What is added here is restraint: a blank is not a zero, so anything that
// rests on a figure nobody recorded is withheld; and statements count only
// once an advisor has accepted them.

const INFLATION = 0.03;
const SUSTAINABLE_RATE = 4;

const fmt = n => `$${Math.round(n).toLocaleString('en-US')}`;
const pct = (n, d = 0) => `${Number(n).toFixed(d)}%`;

// A Social Security row counts once its amount is known, and an explicit zero
// is an answer: "no benefit" is not the same as "nobody asked".
function socialSecurityRecorded(facts) {
  return facts.income.rows.some(r => r.table === 'socialSecurity' && isKnown(r.amount));
}

// Every row of one income table carries an amount.
function tableKnown(facts, table) {
  const rows = facts.income.rows.filter(r => r.table === table);
  return rows.length > 0 && rows.every(r => isKnown(r.amount));
}

// Someone the questionnaire says is working, with no salary recorded. This
// year's cash picture then leaves out income that exists, so nothing about
// savings lasting or running out can be read from it.
function workingWithoutSalary(data, facts) {
  const working = who => {
    const status = String(data?.employment?.[who]?.status || '');
    return /employ/i.test(status) && !/not\s*employ|unemploy/i.test(status);
  };
  const anyWorking = working('client1') || (facts.hasSpouse && working('client2'));
  return anyWorking && !facts.income.rows.some(r => r.table === 'employment' && isKnown(r.amount) && r.amount.value > 0);
}

// Which account categories the questionnaire actually answers: a balance
// entered (zero included), or the section marked "asked, has none". Anything
// else is not recorded, and is never shown as $0.
const POOL_TABLES = { taxDeferred: ['taxDeferred'], taxFree: ['roth', 'hsa'], taxable: ['taxable', 'cashCd'] };
function poolAnswered(data) {
  const assets = data?.assets || {};
  const marked = new Set(Array.isArray(assets.noneOf) ? assets.noneOf : []);
  const entered = t => (Array.isArray(assets[t]) ? assets[t] : []).some(r => r && isKnown(readAmount(r.marketValue)));
  return Object.fromEntries(Object.entries(POOL_TABLES).map(([pool, tables]) => [
    pool, tables.some(entered) || tables.some(t => marked.has(t)),
  ]));
}

// Social Security is the income nearly every household retires on. With no
// row for it, the older selectors read the retired years as if the benefit
// were zero, which overstates the shortfall by the whole benefit and dates the
// savings running out years too early. Anything read from those years waits
// for it instead.
function withheldGoalSheet(sheet, facts, data, answered, hasHsa) {
  if (!sheet) return null;
  const spendingKnown = isKnown(facts.spending);
  // An unresolved sheet reads this year, when the benefit may not matter yet.
  const waitingOnSocialSecurity = sheet.income.period !== 'unresolved' && !socialSecurityRecorded(facts);
  // A blank income row counts as nothing in the total, as the timeline already
  // counts it: one unrecorded line used to turn the total and the surplus into
  // "Not recorded" too (Jimmy, 2026-11-02). The line itself still says Not
  // recorded. Social Security keeps its own wording below. With no income
  // recorded anywhere (not even a zero) there is no total to give, as with the
  // liquid investments.
  const anyIncome = sheet.income.totalIncome !== 0 || facts.income.rows.some(r => isKnown(r.amount));
  const incomeKnown = !waitingOnSocialSecurity && anyIncome;
  // A benefit with an amount and no starting age is counted from today by the
  // timeline. That is an assumption, and the sheet says so.
  const ssRows = Array.isArray(data?.income?.socialSecurity) ? data.income.socialSecurity.filter(Boolean) : [];
  const socialSecurityStartAssumed = ssRows.some(r => isKnown(readAmount(r.annualAmount))
    && !(parseFloat(String(r.startingAge ?? '').replace(/,/g, '')) > 0));
  const bothKnown = spendingKnown && incomeKnown;
  // When Social Security is the only thing missing, the income that is
  // recorded can still be shown, as what there is so far: the slide reads
  // "$24,000 + Social Security" rather than "Needs Social Security" (Jimmy,
  // 2026-10-01). The timeline already counted the unrecorded benefit as
  // nothing, so its total for the year is exactly the rest.
  const incomeSoFar = waitingOnSocialSecurity ? sheet.income.totalIncome : null;
  const income = {
    ...sheet.income,
    requiredToday: spendingKnown ? sheet.income.requiredToday : null,
    requiredAtRetirement: spendingKnown ? sheet.income.requiredAtRetirement : null,
    // A source nobody recorded is not a $0 source. Social Security is always
    // listed, because it is the line every advisor reads first, and it says so
    // when it was never entered.
    // A line of its own (a pension, another income) is recorded when its own
    // row carries an amount; a line that stands for a whole table needs every
    // row of the table.
    sources: sheet.income.sources.map(s => {
      const recorded = s.index === undefined
        ? tableKnown(facts, s.key)
        : isKnown(readAmount(data?.income?.[s.key]?.[s.index]?.annualAmount));
      return { ...s, recorded, amount: recorded ? s.amount : null };
    }),
    totalIncome: incomeKnown ? sheet.income.totalIncome : null,
    shortfall: bothKnown ? sheet.income.shortfall : null,
    // The last line of section 2 is a surplus or a shortfall, never a zero
    // shortfall (Jake, 2026-09-29). Both before tax and before inflation.
    surplus: bothKnown ? Math.max(0, sheet.income.totalIncome - sheet.income.requiredAtRetirement) : null,
    waitingOnSocialSecurity,
    incomeSoFar,
    socialSecurityStartAssumed,
  };
  const anyAnswered = Object.values(answered).some(Boolean);
  return {
    ...sheet,
    liquid: {
      taxDeferred: answered.taxDeferred ? sheet.liquid.taxDeferred : null,
      taxFree: answered.taxFree ? sheet.liquid.taxFree : null,
      taxable: answered.taxable ? sheet.liquid.taxable : null,
      // The sum of what is recorded, an unanswered category adding nothing;
      // with no category answered at all there is no total to give.
      total: anyAnswered ? sheet.liquid.total : null,
      // An HSA is tax-free for medical costs and is not a Roth, so the line
      // says what is in it.
      taxFreeLabel: hasHsa ? 'Tax-free investments (Roth and HSA)' : 'Tax-free Roth investments',
    },
    income,
  };
}

// Which page each of the older report's findings belongs to, so nothing here
// has to be arranged by hand. Every entry is decided and worded by a
// selector; this only routes.
const HOME = {
  cash: ['noGap', 'covered', 'depletion'],
  tax: ['rmdTimer', 'rothWindow', 'assetLocation', 'taxFreeShare'],
  investments: ['concentrationRisk', 'diversified', 'overlap', 'singleCompany'],
  // The fund-against-fund price comparison is left out of the meeting
  // (Jimmy, 2026-09-29), and so are the two findings that restate it.
  costs: ['lowCost', 'feeDrag', 'expensiveHolding'],
};
const TONE = { working: 'good', levers: 'lever', risks: 'risk', attention: 'warn' };

// The cash page shows its findings as headings alone (Jimmy, 2026-10-01), so
// each one has to say what it means without a line under it. The tax page was
// the same until it became follow-up reading (2026-10-02), where the line
// under each heading is printed again; the headings keep this wording. The
// older report keeps its own.
const MEETING_HEADLINES = {
  noGap: () => 'Your income already covers your spending',
  covered: () => 'Even with no growth, your savings last to age 95',
  depletion: h => h.replace(/^The savings run out/, 'Your savings run out'),
  // The amount is today's balance walked forward with no growth, so decades
  // from retirement it is left out and only the year is said.
  rmdTimer: (h, x) => (x.rmd?.alreadyStarted
    ? 'Required withdrawals from your tax-deferred accounts have started, and are taxed as income'
    : x.far
      ? `From ${x.rmd.firstYear}, withdrawals from your tax-deferred accounts are required, and are taxed as income`
      : `From ${x.rmd.firstYear}, you must withdraw about ${fmt(Math.round(x.rmd.firstAmount / 1000) * 1000)} a year from your tax-deferred accounts, taxed as income`),
  rothWindow: (h, x) => `You have ${x.roth.windowYears} ${x.roth.windowYears === 1 ? 'year' : 'years'} to convert to Roth on your own schedule, before required withdrawals start in ${x.roth.windowClosesIn}`,
  assetLocation: (h, x) => `${fmt(x.location.misplacedValue)} of investments could sit in accounts where they are taxed less`,
};
const TONE_ORDER = { risk: 0, warn: 1, lever: 2, note: 3, good: 4 };

function routeSituation(situation, context = {}) {
  const out = { cash: [], tax: [], investments: [], costs: [] };
  for (const [box, items] of Object.entries(situation || {})) {
    for (const item of items || []) {
      const page = Object.keys(HOME).find(k => HOME[k].includes(item.key));
      if (!page) continue;
      out[page].push({ key: item.key, tone: TONE[box] || 'note', headline: MEETING_HEADLINES[item.key]?.(item.headline, context) ?? item.headline, detail: item.detail });
    }
  }
  return out;
}

// The withdrawal rate the planning team means: what savings must supply each
// year once everyone is retired and every benefit has started, after Social
// Security and pensions, as a share of savings. It is the same whichever years
// the slide is reading, so the year before Social Security starts cannot make
// it look like the household draws a fifth of its savings every year.
function steadyDraw(sheet, timeline) {
  const { income, liquid } = sheet;
  if (income.period === 'unresolved' || income.shortfall === null) return null;
  const year = income.bridge ? income.bridge.toYear + 1 : income.atYear;
  const row = timeline.find(r => r.year === year);
  if (!row) return null;
  const shortfall = Math.max(0, row.spendingNeed - row.totalIncome);
  return { year, shortfall, rate: liquid.total > 0 ? (shortfall / liquid.total) * 100 : null };
}

// The cash-flow observations Jake's deck makes generically ("withdrawing more
// than 4% a year", "inflation erodes fixed income"), computed for this
// household so each one carries its own number. Each is withheld when the
// figure it needs was never recorded.
function cashCallouts(sheet, facts, timeline) {
  const out = [];
  if (!sheet) return out;
  const { income, liquid } = sheet;
  const shortfall = income.shortfall;
  const spending = isKnown(facts.spending) ? facts.spending.value : null;

  // Decades from retirement, today's savings are not what the household will
  // retire on, so nothing here sets one against the other.
  const far = isFarFromRetirement(income);

  // The withdrawal rate, against the 4% the planning wiki treats as on track.
  // Said in full, because it stands as a heading on its own: what share of
  // savings retirement takes each year, and which side of the guide that is.
  const draw = far ? null : steadyDraw(sheet, timeline);
  if (draw && draw.rate !== null && draw.rate > 0) {
    const settled = income.alreadyThere && !income.bridge;
    const over = draw.rate > SUSTAINABLE_RATE;
    out.push({
      key: 'withdrawalRate',
      tone: over ? 'warn' : 'good',
      headline: `${settled ? 'You draw' : 'In retirement you would draw'} ${pct(draw.rate, 1)} of your savings each year, ${over ? 'above' : 'within'} the ${SUSTAINABLE_RATE}% guideline`,
    });
  }

  // Anchored to the year retirement starts, not to the year section 2 is read
  // from: with the steady period chosen, the read year is after the bridge,
  // and five years from there are not the first five years of retirement.
  // Held back decades out like the withdrawal rate: it is read with no growth
  // and no saving still to come (rules catalog, 2026-10-19).
  if (!far && shortfall !== null && income.period !== 'unresolved') {
    const firstYear = income.alreadyThere ? timeline[0]?.year : (income.bridge?.fromYear ?? income.atYear);
    const start = timeline.findIndex(r => r.year === firstYear);
    if (start >= 0) {
      const span = timeline.slice(start, start + 5);
      const five = span.reduce((s, r) => s + Math.max(0, r.gap), 0);
      if (five > 0 && span.length === 5) {
        out.push({
          key: 'firstFiveYears',
          tone: 'note',
          headline: income.alreadyThere
            ? `Your next five years need ${fmt(five)} from savings`
            : `Your first five years of retirement need ${fmt(five)} from savings`,
        });
      }
    }
  }

  // To the year retirement starts, whichever years the Goals slide reads.
  // Not said decades out, where it would inflate the spending and leave the
  // savings beside it as they are today.
  if (spending !== null && spending > 0 && !far) {
    const thisYear = timeline[0]?.year;
    const starts = income.period === 'unresolved' || income.alreadyThere ? null : (income.bridge?.fromYear ?? income.atYear);
    const years = starts === null || !Number.isFinite(thisYear) ? 0 : starts - thisYear;
    if (years >= 3) {
      const future = Math.round((spending * (1 + INFLATION) ** years) / 1000) * 1000;
      out.push({
        key: 'inflation',
        tone: 'note',
        headline: `At ${pct(INFLATION * 100)} inflation, your ${fmt(spending)} of spending becomes about ${fmt(future)} by ${starts}`,
      });
    } else {
      out.push({
        key: 'inflation',
        tone: 'note',
        headline: `At ${pct(INFLATION * 100)} inflation, your spending rises about ${fmt(spending * INFLATION)} a year`,
      });
    }
  }

  const ss = facts.income.rows.filter(r => r.table === 'socialSecurity' && isKnown(r.amount));
  const ssTotal = ss.reduce((s, r) => s + r.amount.value, 0);
  // Not for a retired household already drawing everything it will get: that
  // decision has been made. Someone still working with no starting age
  // recorded has not made it, whatever year the timeline counts it from.
  const drawingNow = timeline[0]?.bySource.socialSecurity || 0;
  const drawingLater = timeline.reduce((max, r) => Math.max(max, r.bySource.socialSecurity), 0);
  const decided = income.alreadyThere && drawingNow >= drawingLater;
  // Past 70 there is nothing left to gain by waiting.
  const ages = [timeline[0]?.c1Age, timeline[0]?.c2Age].filter(a => a !== null && a !== undefined);
  const pastSeventy = ages.length > 0 && ages.every(a => a >= 70);
  if (ssTotal > 0 && !decided && !pastSeventy) {
    out.push({
      key: 'ssTiming',
      tone: 'note',
      // The delayed retirement credit: two-thirds of 1% a month past full
      // retirement age, for anyone born in 1943 or later (SSA).
      headline: `Your ${fmt(ssTotal)} of Social Security grows about 8% for each year you wait past full retirement age, up to 70`,
    });
  }
  return out;
}

// How the money is invested, read against when it is needed (Jimmy and John,
// 2026-10-01): the portfolio analysis and the questionnaire side by side,
// which neither the statement tools nor the planning software do. Each one
// needs a retirement date and is put as a question for planning, never as a
// recommendation. Shares are of everything analyzed, so cash counts as the
// safe money it is and does not hide inside a stock-to-bond ratio.
const STOCKS_NEAR_RETIREMENT = 85;
const STOCKS_WINDOW_YEARS = 5;
const SAFE_EARLY = 60;
const EARLY_YEARS = 20;

function timingCallouts(allocation, income) {
  const out = [];
  if (!allocation || !(allocation.total > 0) || !income || income.period === 'unresolved') return out;
  const stocks = (allocation.equity / allocation.total) * 100;
  const safe = ((allocation.bonds + (allocation.cash || 0)) / allocation.total) * 100;
  // To the year retirement starts, not to the year the cash slide is read
  // from, which is later when the advisor reads it once every benefit has
  // started.
  if (!Number.isFinite(income.atYear) || !Number.isFinite(income.yearsAway)) return out;
  const thisYear = income.atYear - income.yearsAway;
  const starts = income.bridge?.fromYear ?? income.atYear;
  const years = income.alreadyThere ? 0 : Math.max(0, starts - thisYear);

  if (stocks >= STOCKS_NEAR_RETIREMENT && years <= STOCKS_WINDOW_YEARS) {
    out.push({
      key: 'stocksNearRetirement',
      tone: 'warn',
      headline: income.alreadyThere
        ? `${pct(stocks)} of your investments are in stocks, and retirement has started`
        : `${pct(stocks)} of your investments are in stocks, ${years} ${years === 1 ? 'year' : 'years'} from retirement`,
      detail: 'A fall like 2008 does the most harm in the first years of retirement, when withdrawals sell what has dropped. '
        + 'How much needs to stay in stocks, and how much should not, is a question for planning.',
    });
  }
  if (safe >= SAFE_EARLY && years >= EARLY_YEARS) {
    out.push({
      key: 'safeEarly',
      tone: 'note',
      headline: `${pct(safe)} of your investments are in bonds and cash, with ${years} years until retirement`,
      detail: 'Bonds and cash hold steadier, but over decades they have grown far less than stocks. '
        + 'Whether this much safety is costing growth you will need is a question for planning.',
    });
  }
  return out;
}

function investmentCallouts(allocation) {
  const out = [];
  if (allocation && allocation.usShareOfEquity !== null && allocation.usShareOfEquity >= 85 && allocation.equity > 0) {
    out.push({
      key: 'usHeavy',
      tone: 'note',
      headline: `${pct(allocation.usShareOfEquity)} of your stock is in the US`,
      detail: 'One market and one currency. Most of the world’s companies are elsewhere, and they do not all move together.',
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The cash-flow slide (rebuilt 2026-10-01).
//
// The slide is the household's years: income by where it comes from, against
// what they spend, from now to the end of the plan. That is the story an
// advisor tells here, and it is what Pure's planning wiki asks for when it
// reads a cash flow: whether there is a gap between retirement and Social
// Security, and what savings have to supply once every benefit has started.
//
// Those stretches used to be a choice beside the slide ("the first years of
// retirement" or "once every benefit has started") that decided which single
// year the figures read. They are now marked on the picture itself, each with
// what savings supply in it, so the advisor points at them rather than picks
// one (Jimmy, 2026-10-01).
const TIMELINE_YEARS = 30;

// What one stretch of years asks of savings, from its first year. A stretch
// whose figures are not all recorded says what is missing instead.
function stretchNote(row, { open = null } = {}) {
  if (open) return open;
  const gap = row.spending - row.income;
  return gap > 0 ? `${fmt(gap)} a year from savings` : 'Income covers spending';
}

function cashTimeline(timeline, sheet, facts, data) {
  if (!sheet || !Array.isArray(timeline)) return null;
  const { income } = sheet;
  const resolved = income.period !== 'unresolved';
  const first = timeline[0]?.year;
  const retireYear = !resolved ? null : income.alreadyThere ? first : (income.bridge?.fromYear ?? income.atYear);

  // Far enough to see the retired years settle, however far off retirement is.
  const settled = timeline.findIndex(r => r.year === (income.bridge ? income.bridge.toYear + 1 : income.atYear));
  const span = Math.min(timeline.length, Math.max(TIMELINE_YEARS, settled + 12));
  const rows = timeline.slice(0, span).map(r => ({
    year: r.year,
    c1Age: r.c1Age,
    c2Age: r.c2Age,
    employment: r.bySource.employment,
    socialSecurity: r.bySource.socialSecurity,
    pension: r.bySource.pension,
    other: r.bySource.other,
    income: r.totalIncome,
    spending: r.spendingNeed,
    retired: retireYear !== null && r.year >= retireYear,
  }));

  const othersKnown = facts.income.rows.filter(r => r.table !== 'socialSecurity').every(r => isKnown(r.amount));
  const state = !isKnown(facts.spending) ? 'spendingMissing'
    : !othersKnown ? 'incomeMissing'
      : income.waitingOnSocialSecurity ? 'socialSecurityMissing'
        : 'ready';
  const salaryMissing = workingWithoutSalary(data, facts);
  const couple = rows[0]?.c2Age !== null && rows[0]?.c2Age !== undefined;
  const last = rows.length ? rows[rows.length - 1].year : null;
  const rowAt = year => rows.find(r => r.year === year);

  // The stretches, in order. Working years, then retirement: split at the
  // year every benefit has started when there are years before it.
  const periods = [];
  if (rows.length) {
    if (retireYear === null) {
      // Nobody knows when they retire, so the years are drawn as they are.
    } else {
      if (retireYear > first) {
        periods.push({
          key: 'working',
          label: 'Working years',
          from: first,
          to: retireYear - 1,
          note: salaryMissing ? 'Salary not recorded' : stretchNote(rows[0]),
        });
      }
      const open = state === 'socialSecurityMissing' ? 'Open until Social Security is added' : null;
      if (income.bridge && income.bridge.toYear >= retireYear) {
        const bridgeFrom = Math.max(first, income.bridge.fromYear);
        periods.push({
          key: 'bridge',
          label: 'The first years of retirement',
          from: bridgeFrom,
          to: income.bridge.toYear,
          note: stretchNote(rowAt(bridgeFrom), { open }),
        });
        if (income.bridge.toYear + 1 <= last) {
          periods.push({
            key: 'steady',
            label: 'Once every benefit has started',
            from: income.bridge.toYear + 1,
            to: last,
            note: stretchNote(rowAt(income.bridge.toYear + 1), { open }),
          });
        }
      } else {
        periods.push({
          key: open ? 'retired' : 'steady',
          label: open ? 'Retirement' : (income.alreadyThere ? 'Retired, every benefit started' : 'Once every benefit has started'),
          from: retireYear,
          to: last,
          note: stretchNote(rowAt(retireYear), { open }),
        });
      }
    }
  }

  // The moments inside a stretch that change the picture. Where a stretch
  // starts is already marked by the stretch, so it is not named twice.
  const boundaries = new Set(periods.map(x => x.from));
  const milestones = [];
  for (let i = 1; i < rows.length; i += 1) {
    const prev = rows[i - 1];
    const now = rows[i];
    if (now.employment < prev.employment && now.employment > 0) milestones.push({ year: now.year, label: 'One of you retires' });
    if (now.socialSecurity > prev.socialSecurity) {
      milestones.push({ year: now.year, label: prev.socialSecurity === 0 ? 'Social Security starts' : 'Second benefit starts' });
    }
    if (now.pension > 0 && prev.pension === 0) milestones.push({ year: now.year, label: 'Pension starts' });
  }

  const ssStarts = rows.length && rows[0].socialSecurity === 0 ? rows.find(r => r.socialSecurity > 0)?.year ?? null : null;

  return {
    rows,
    state,
    salaryMissing,
    couple,
    retireYear,
    alreadyRetired: Boolean(income.alreadyThere),
    socialSecurityFrom: ssStarts,
    periods,
    milestones: milestones.filter(m => !boundaries.has(m.year)).slice(0, 4),
    spendingToday: isKnown(facts.spending) ? income.requiredToday : null,
    socialSecurityStartAssumed: Boolean(income.socialSecurityStartAssumed),
  };
}

function sortCallouts(items, hidden) {
  return items
    .map(i => ({ ...i, hidden: hidden.has(i.key) }))
    .sort((a, b) => (TONE_ORDER[a.tone] ?? 3) - (TONE_ORDER[b.tone] ?? 3));
}

// Retiring before 65 leaves a gap before Medicare, and the questionnaire has
// no field for how it is covered, so it is an item to discuss rather than a
// fact. Beneficiary designations and titling are the same: nothing on the
// questionnaire records them, and Jake's deck lists them as "verify and
// update", which is what the row says.
function extendProtection(checklist, { medicareGap }) {
  if (!checklist) return null;
  const coverage = [...checklist.coverage];
  if (medicareGap) {
    coverage.push({
      key: 'Medicare',
      label: 'Health coverage before Medicare',
      why: 'Retiring before 65 means buying your own health coverage until Medicare starts, and it is rarely cheap.',
      status: 'unknown',
      fixed: true,
    });
  }
  const docs = [
    ...checklist.docs,
    {
      key: 'Titling',
      label: 'Beneficiary designations and account titling',
      why: 'A beneficiary form overrides the will. An account in the wrong name, or with a stale beneficiary, goes where nobody intended.',
      status: 'verify',
      fixed: true,
    },
  ];
  const all = [...docs, ...coverage];
  const counts = {
    inPlace: all.filter(i => i.status === 'in-place').length,
    none: all.filter(i => i.status === 'none').length,
    unknown: all.filter(i => i.status === 'unknown').length,
  };
  return { ...checklist, docs, coverage, counts, anythingKnown: counts.inPlace + counts.none > 0 };
}

function medicareGapFor(data, assumptions) {
  const age = v => { const n = parseFloat(String(v ?? '').replace(/,/g, '')); return Number.isFinite(n) ? n : 0; };
  const people = [
    [age(data?.family?.client1Age), assumptions.c1RetireAge],
    [age(data?.family?.client2Age), assumptions.c2RetireAge],
  ];
  return people.some(([now, retire]) => now > 0 && retire > 0 && retire < 65 && now < 65);
}

// The advisor's choices for this meeting, kept on the assessment record: which
// years section 2 reads from, which findings are left out, the advisory fee
// from the statement until the statement reader extracts it, whether the
// tax page sets out the triangle or leaves the advisor room to draw their own
// (on unless turned off; Jimmy, 2026-10-01), and the heading the goal sheet
// opens with when the advisor has written their own.
export function readMeeting(data) {
  const m = data?.assessment?.meta?.meeting || {};
  const fee = parseFloat(String(m.advisoryFeePct ?? '').replace(/[%,\s]/g, ''));
  return {
    period: m.period || null,
    triangle: m.triangle !== false,
    goalLead: String(m.goalLead || '').trim() || null,
    // The goals section is on the page unless the advisor took it off
    // (Jimmy, 2026-10-20).
    goalsShown: m.goalsSection !== false,
    // The goals page's own list once the advisor has shaped it (reviewed
    // Claude's goals, or edited them on the page); the questionnaire's goals
    // until then (Jimmy, 2026-10-20).
    goals: Array.isArray(m.goals) ? m.goals.map(g => String(g || '').trim()).filter(Boolean) : null,
    hidden: new Set(Array.isArray(m.hidden) ? m.hidden : []),
    advisoryFeePct: Number.isFinite(fee) && fee >= 0 ? fee : null,
  };
}

// The older report let a client move sliders, saved as `assessment.whatIf`, and
// every figure read through them. This screen has no sliders and no way to see
// one is set, so an old override would silently replace the questionnaire's
// own spending or retirement age (QA review, 2026-09-30). It is set aside here
// and left in place for the Classic view that still uses it.
function withoutScenario(data) {
  if (!data?.assessment?.whatIf) return data;
  const { whatIf, ...assessment } = data.assessment;
  return { ...data, assessment };
}

export function buildAssessmentView(input, { facts, comparison, accepted = false } = {}) {
  const data = withoutScenario(input);
  const meeting = readMeeting(data);
  const assumptions = getAssessmentAssumptions(data);
  const timeline = buildIncomeTimeline(data, assumptions);
  const taxPools = getTaxPoolSnapshot(data);
  const summary = getGapSummary(timeline, {
    client1Name: (data?.family?.client1FirstName || '').trim() || null,
    liquidAssets: taxPools.total,
    assumptions,
  });

  const bundle = accepted ? getHoldingsBundle(data) : null;
  const fees = analyzeFees(bundle);
  const rmd = taxPools.total > 0 ? getRmdOutlook(data) : null;
  const location = analyzeTaxLocation(bundle);
  const lookThrough = bundle?.lookThrough || null;
  const feeComparison = compareFeeAlternatives(bundle);
  const roth = getRothConversionOutlook(rmd, { annual: 0, years: 0 });
  // The Goals slide reads the first years of retirement when there are any.
  // The buttons that switched it are gone (Jimmy, 2026-10-01), so a choice
  // saved with them is set aside rather than left to change the slide with no
  // way to see or undo it.
  const rawSheet = getGoalSheet(data, { assumptions, timeline, taxPools, period: null, perRow: true });
  const answered = poolAnswered(data);
  const hasHsa = taxPools.pools.some(p => (p.rows || []).some(r => r.table === 'hsa'));
  const goalSheet = withheldGoalSheet(rawSheet, facts, data, answered, hasHsa);
  const protection = extendProtection(getProtectionChecklist(data), {
    medicareGap: medicareGapFor(data, assumptions),
  });
  const allocation = getAllocation(bundle);

  // The cash-flow findings (income covers spending, the savings outlast the
  // plan, the year they run out) all read spending against income. Without
  // both recorded there is nothing honest to say, so the summary is given no
  // cash-flow inputs at all and those boxes stay empty rather than invented.
  const cashKnown = isKnown(facts.spending) && facts.spending.value > 0 && facts.income.everyRowKnown
    && !workingWithoutSalary(data, facts);
  // This year's comparison stands on its own; whether the savings last reads
  // the retired years, so it waits for Social Security like the goal sheet.
  const cashSummary = cashKnown
    ? (socialSecurityRecorded(facts) ? summary : { ...summary, coverage: null })
    : null;
  const situation = getSituationSummary({
    taxPools: taxPools.total > 0 ? taxPools : null,
    summary: cashSummary,
    rmd,
    location,
    fees,
    lookThrough,
    assumptions,
    // The fund-against-fund comparison is not shown in the meeting, but the
    // summary lets it stand in for the expensive-fund finding as one fact in
    // two boxes, so with it the meeting showed neither: a costly fund went
    // unmentioned whenever a cheaper one sat in the same class (2026-10-01).
    feeComparison: null,
    roth,
  }, { only: Object.values(HOME).flat() });

  const far = isFarFromRetirement(goalSheet?.income);
  const routed = routeSituation(situation, { rmd, roth, location, far });
  // Whether today's savings last, or the year they run out, is read with no
  // growth and no saving still to come, so it is not said decades out.
  const NO_GROWTH = ['covered', 'depletion'];
  const callouts = {
    // "Income already covers your spending" is the expected state while
    // someone is still working, so it is said only of a retired household.
    cash: sortCallouts(
      [
        ...routed.cash.filter(c => (c.key !== 'noGap' || goalSheet?.income.alreadyThere) && !(far && NO_GROWTH.includes(c.key))),
        ...cashCallouts(goalSheet, facts, timeline),
      ],
      meeting.hidden,
    ),
    // The banner that used to open the tax slide is a finding like the rest,
    // so it is said once and can be left out.
    tax: sortCallouts([
      ...(taxPools.total > 0 && taxPools.deferredSharePct >= 50 ? [{
        key: 'untaxedShare',
        tone: 'warn',
        headline: `${Math.round(taxPools.deferredSharePct)}% of your savings has never been taxed, and will be taxed as income when withdrawn`,
        detail: 'Every dollar taken from a tax-deferred account is taxed as ordinary income, and required withdrawals eventually set the timing for you.',
      }] : []),
      ...routed.tax,
    ], meeting.hidden),
    investments: sortCallouts([...routed.investments, ...investmentCallouts(allocation), ...timingCallouts(allocation, goalSheet?.income)], meeting.hidden),
    // "Your fund costs are genuinely low" beside "one fund is charging you
    // 0.92%" reads as the page arguing with itself; the fund is the point.
    costs: sortCallouts(
      routed.costs.some(c => c.key === 'expensiveHolding') ? routed.costs.filter(c => c.key !== 'lowCost') : routed.costs,
      meeting.hidden,
    ),
  };

  // The advisory fee is on the statement, not in the questionnaire, so it is
  // entered on the assessment itself until the statement reader extracts it.
  // It is applied to the analyzed value, which is the statement accounts it
  // was read from.
  //
  // All-in is added in dollars, on one base, and its percentage is those
  // dollars over the analyzed value, so the two always reconcile. Adding the
  // fund average (over holdings with a published ratio) to the advisory rate
  // (over everything) mixed two bases (QA review, 2026-09-30). Fund costs that
  // are not published are not counted, so the figure is a floor whenever any
  // are missing, and it is not built at all when none are published.
  const base = fees?.totalValue || 0;
  const advisory = {
    pct: meeting.advisoryFeePct,
    dollars: meeting.advisoryFeePct !== null && base > 0 ? (base * meeting.advisoryFeePct) / 100 : null,
    base,
  };
  const allIn = fees && advisory.pct !== null && fees.weightedExpenseRatioPct !== null && base > 0
    ? (() => {
      const dollars = fees.annualFeeDollars + (advisory.dollars || 0);
      return { dollars, pct: (dollars / base) * 100, complete: fees.coveragePct >= 99.995 };
    })()
    : null;

  // The opening figures, Jake's snapshot slide: the three pools, their total,
  // and what a year costs now and at retirement.
  const snapshot = {
    pools: taxPools.pools.map(p => ({
      key: p.key,
      label: p.label,
      value: answered[p.key] ? p.value : null,
      share: answered[p.key] && taxPools.total > 0 ? Math.round((p.value / taxPools.total) * 100) : null,
    })),
    hasHsa,
    total: taxPools.total > 0 ? taxPools.total : null,
    spendingToday: goalSheet?.income.requiredToday ?? null,
    spendingAtRetirement: goalSheet && !goalSheet.income.alreadyThere && goalSheet.income.period !== 'unresolved'
      ? goalSheet.income.requiredAtRetirement
      : null,
    yearsAway: goalSheet?.income.yearsAway ?? null,
  };

  // The close's figures: what planning would work on, each only when known.
  // This was "the cost of waiting", which implied the figures go away with
  // planning; fees and protection gaps do not (Jimmy and John, 2026-10-01).
  // Fees are left to the cost slide for the same reason.
  const workingOn = [];
  if (goalSheet?.income.shortfall > 0) {
    workingOn.push({ key: 'shortfall', value: fmt(goalSheet.income.shortfall), label: 'a year needed from savings in retirement, before tax' });
  }
  if (taxPools.total > 0 && taxPools.deferredSharePct > 0) {
    workingOn.push({ key: 'deferred', value: pct(taxPools.deferredSharePct), label: 'of your savings still to be taxed as income' });
  }
  const gaps = (protection?.counts.none || 0) + (protection?.counts.unknown || 0);
  if (gaps > 0) {
    workingOn.push({ key: 'gaps', value: String(gaps), label: `protection ${gaps === 1 ? 'item' : 'items'} missing or not yet discussed` });
  }

  // The stage of life the household is at, and the heading the goal sheet
  // opens with because of it (Amanda Cook, 2026-10-02).
  const profile = getHouseholdProfile({ facts, income: goalSheet?.income || null, liquid: taxPools.total });

  return {
    profile,
    goalLead: getGoalLead(profile, meeting.goalLead),
    snapshot,
    taxPools: { ...taxPools, pools: taxPools.pools.map(p => ({ ...p, hasHsa: p.key === 'taxFree' && hasHsa })) },
    goalSheet,
    situation,
    callouts,
    fees,
    feeComparison,
    advisory,
    allIn,
    unpriced: summarizeUnpricedHoldings(bundle),
    bundle,
    allocation,
    protection,
    planQuestions: getPlanQuestions(data, {
      goalSheet: rawSheet && isKnown(facts.spending) ? rawSheet : null,
      rmd,
      roth,
      taxPools,
      protection,
    }),
    workingOn,
    cashTimeline: cashTimeline(timeline, goalSheet, facts, data),
    cashKnown,
    // What arrives this year, whether or not anyone works: the older report's
    // "income you can count on", which leaves out benefits not yet started.
    incomeNow: timeline[0]?.totalIncome || 0,
    meeting,
  };
}
